/**
 * AnnouncementBar — site-wide operational notice, driven by the admin
 * CMS keys `site.announcement.{text, link_label, link_url}` and served
 * by `GET /api/public/cms/content-blocks`.
 *
 * Contract (from 2026-07 spec):
 *   • Text-only (kind: "text"). Never dangerouslySetInnerHTML.
 *   • Empty text → nothing renders (no reserved space, no CLS).
 *   • CTA only when BOTH link_label and link_url are non-empty.
 *     Internal URLs (leading /) use client-side <Link>; external open
 *     in a new tab with rel="noopener noreferrer".
 *   • Dismissible per session, keyed by a short hash of the current
 *     text — a NEW announcement re-appears even after a prior dismiss.
 *   • role="region" + aria-label; ✕ is a real <button> with
 *     aria-label + visible keyboard focus ring.
 *   • Client-only render (no SSR hydration mismatch — the fetch is
 *     browser-only, so the component always mounts as invisible on
 *     first client tick and only paints if text is non-empty).
 *   • Hidden on /admin routes so authors editing the announcement in
 *     the CMS aren't distracted by a live copy of what they're editing.
 *
 * CLS treatment (2026-02 PageSpeed fix):
 *   • Overlays the top of the page rather than pushing the Header
 *     down. The Header stays at `top: 0` and the bar stacks above it
 *     at z=1001. Approach matches the user directive "overlay them
 *     instead of pushing content".
 *   • A `localStorage` cache (`ac_announcement`) is read SYNCHRONOUSLY
 *     during the initial render for repeat visitors so the bar paints
 *     in the first commit (zero shift). First-time visitors still see
 *     the bar fade in after the fetch, but because the bar overlays
 *     rather than pushes, there is still no layout shift of
 *     page content — only the overlay itself fades in.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useContentBlocks } from "../hooks/useContentBlocks";

// djb2-style hash — small, stable, no crypto. Good enough for keying
// sessionStorage; collisions here just mean two very-similar
// announcements share a dismiss slot, which is acceptable.
function shortHash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = (h * 33) ^ str.charCodeAt(i);
  return (h >>> 0).toString(36);
}

export default function AnnouncementBar() {
  const blocks = useContentBlocks();
  const { pathname } = useLocation();

  // Never render on the server-rendered pass; only after client mount.
  // This prevents any theoretical hydration mismatch if a prerender
  // ever tries to bake this into HTML (currently it doesn't).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const text  = (blocks["site.announcement.text"] || "").trim();
  const label = (blocks["site.announcement.link_label"] || "").trim();
  const url   = (blocks["site.announcement.link_url"] || "").trim();

  // Re-key dismissal whenever text changes so a fresh emergency notice
  // isn't suppressed by yesterday's dismissal.
  const dismissKey = useMemo(() => `ac.announce.dismiss.${shortHash(text)}`, [text]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!text) { setDismissed(false); return; }
    try {
      setDismissed(sessionStorage.getItem(dismissKey) === "1");
    } catch {
      // Storage disabled (private mode, ITP, etc.) — treat as fresh.
      setDismissed(false);
    }
  }, [text, dismissKey]);

  // Publish this bar's height as `--ac-banner-offset` on <html> so the
  // fixed site header can shift down, and other overlays already
  // consuming this variable (DiscoveryChatBubble, etc.) stay clear.
  // Cleared on unmount so navigating to a route that hides the bar
  // (currently just /admin/*) restores the default zero offset.
  // Publish the measured bar height to a dedicated CSS var so OTHER
  // overlays can offset themselves against the top bar if they ever
  // need to. We intentionally do NOT push the Header down — the bar
  // overlays — so this var is informational, not structural. Writing
  // only once per text/dismiss change keeps CLS at zero: no fixed
  // element's `top`/`bottom` depends on this value.
  useEffect(() => {
    if (!text || dismissed || !mounted || (pathname && pathname.startsWith("/admin"))) {
      document.documentElement.style.setProperty("--ac-topbar-height", "0px");
      return;
    }
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector('[data-testid="site-announcement-bar"]');
      if (el) {
        document.documentElement.style.setProperty("--ac-topbar-height", `${el.offsetHeight}px`);
      }
    });
    return () => {
      cancelAnimationFrame(raf);
      document.documentElement.style.setProperty("--ac-topbar-height", "0px");
    };
  }, [text, dismissed, mounted, pathname]);

  // Guard rails — every early return corresponds to a spec bullet.
  if (!mounted) return null;                     // no SSR / hydration flash
  if (pathname && pathname.startsWith("/admin")) return null;  // admin has own chrome
  if (!text) return null;                        // empty CMS value → render nothing
  if (dismissed) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(dismissKey, "1"); } catch { /* storage blocked */ }
    setDismissed(true);
  };

  const hasCTA     = !!(label && url);
  const isInternal = hasCTA && url.startsWith("/");

  return (
    <div
      role="region"
      aria-label="Site announcement"
      data-testid="site-announcement-bar"
      className="fixed top-0 left-0 right-0 z-[1001] w-full bg-[#0F2750] text-white shadow-[0_1px_0_rgba(255,255,255,0.06)_inset] motion-safe:transition-opacity"
    >
      <div className="ac-container flex items-center gap-3 py-2 md:py-2.5">
        <p
          className="flex-1 min-w-0 text-[13px] md:text-[14px] leading-snug"
          data-testid="site-announcement-text"
        >
          {text}
          {hasCTA && (
            <>
              {" "}
              {isInternal ? (
                <Link
                  to={url}
                  className="ml-1 font-bold underline underline-offset-2 hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-sm"
                  data-testid="site-announcement-cta"
                >
                  {label} →
                </Link>
              ) : (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 font-bold underline underline-offset-2 hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-sm"
                  data-testid="site-announcement-cta"
                >
                  {label} →
                </a>
              )}
            </>
          )}
        </p>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss announcement"
          data-testid="site-announcement-dismiss"
          className="inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-md text-white/80 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F2750] motion-safe:transition"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
